import React, { useEffect, useState } from 'react';
import { DealsApiService } from '@vanilla/services/crm/DealsApiService.js';
import { ToastManager } from '@/lib/toast.js';
import { Input } from '@/components/ui/input';
import { NativeSelect } from '@/components/ui/select';
import AutoGrowTextarea from '@/components/ui/AutoGrowTextarea.jsx';
import ActivityFormModal from './ActivityFormModal.jsx';

/**
 * Add/Edit Next Step dialog. When dealId is missing, showDealPicker + dealOptions enable calendar quick-create.
 */
export default function NextStepFormDialog({
    open,
    mode = 'add',
    dealId: dealIdProp,
    initial = null,
    sortOrder = 0,
    showDealPicker = false,
    dealOptions = [],
    onSaved,
    onClose,
}) {
    const [action, setAction] = useState('');
    const [dueDate, setDueDate] = useState('');
    const [dealId, setDealId] = useState(dealIdProp || '');

    useEffect(() => {
        if (!open) return;
        setAction(initial?.action || '');
        setDueDate(initial?.date || initial?.dueDate ? String(initial.date || initial.dueDate).slice(0, 10) : '');
        setDealId(dealIdProp || initial?.dealId || '');
    }, [open, initial, dealIdProp]);

    if (!open) return null;

    return (
        <ActivityFormModal
            title={mode === 'add' ? 'Add Next Step' : 'Edit Next Step'}
            onClose={onClose}
            onSave={async () => {
                const trimmed = action.trim();
                if (!trimmed) return false;
                const effectiveDealId = dealId || dealIdProp;
                if (!effectiveDealId) {
                    ToastManager.show({ title: 'Next step', message: 'Select a deal first.', tone: 'warning' });
                    return false;
                }
                try {
                    if (mode === 'add') {
                        await DealsApiService.createNextStep(effectiveDealId, {
                            action: trimmed,
                            due_date: dueDate || null,
                            is_completed: 'false',
                            sort_order: sortOrder,
                        });
                    } else if (initial?.id) {
                        await DealsApiService.updateNextStep(effectiveDealId, initial.id, {
                            action: trimmed,
                            due_date: dueDate || null,
                        });
                    }
                    await onSaved?.({ dealId: effectiveDealId });
                    return true;
                } catch (err) {
                    ToastManager.show({ title: 'Next step', message: err?.message || 'Save failed.', tone: 'error' });
                    return false;
                }
            }}
        >
            {showDealPicker && !dealIdProp ? (
                <div className="field-group">
                    <label>Deal <span className="required-asterisk">*</span></label>
                    <NativeSelect unstyled className="form-input" value={dealId} onChange={(e) => setDealId(e.target.value)}>
                        <option value="">Select deal…</option>
                        {dealOptions.map((d) => (
                            <option key={d.id} value={d.id}>{d.name || d.dealName || d.id}</option>
                        ))}
                    </NativeSelect>
                </div>
            ) : null}
            <div className="field-group">
                <label>Action <span className="required-asterisk">*</span></label>
                <AutoGrowTextarea
                    value={action}
                    onChange={(e) => setAction(e.target.value)}
                    placeholder="e.g. Follow up with client"
                    minRows={2}
                    maxHeightPx={160}
                    aria-label="Next step action"
                />
            </div>
            <div className="field-group">
                <label>Due Date</label>
                <Input unstyled type="date" className="form-input" value={dueDate} onChange={(e) => setDueDate(e.target.value)} />
            </div>
        </ActivityFormModal>
    );
}
